/**
 * AvatarCustomizer — /dashboard/avatar-builder
 * =============================================
 * Neural "Digital Twin" builder.
 *
 * Workflow
 *   1. Identity  — upload a professional headshot (drag-and-drop or click)
 *                  Backend pre-processes it to 512×512 JPEG.
 *   2. Voice DNA  — record 10–20 s of speech; backend extracts an XTTS v2
 *                   speaker embedding so the AI sounds like the real agent.
 *   3. Persona    — set idle-loop intensity and the AI system prompt.
 *   4. Preview    — call backend to generate a 3-second idle animation
 *                   (MuseTalk when configured, static image otherwise).
 *   5. Save       — persist the full profile to the server.
 */

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  CloudUpload,
  Mic,
  Person,
  PlayArrow,
  Refresh,
  Save,
  Stop,
} from '@mui/icons-material';

// ─── Constants ────────────────────────────────────────────────────────────────

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001/api/v1';
const API_ORIGIN = (() => { try { return new URL(API).origin; } catch { return 'http://localhost:8001'; } })();
const MAX_RECORD_S = 20;

type IdleIntensity = 'calm' | 'normal' | 'energetic';

interface DigitalTwinProfile {
  has_face_image:      boolean;
  has_voice_embedding: boolean;
  idle_intensity:      IdleIntensity;
  system_prompt:       string;
  face_image_url:      string | null;
  vapi_voice_provider: string;
  vapi_voice_id:       string;
}

type RecordState = 'idle' | 'recording' | 'processing' | 'done' | 'error';
type Toast = { msg: string; sev: 'success' | 'error' | 'info' };

// ─── Component ────────────────────────────────────────────────────────────────

const AvatarCustomizer: React.FC = () => {
  // ── Remote profile ─────────────────────────────────────────────────────────
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);

  // ── Identity ───────────────────────────────────────────────────────────────
  const [imagePreview,    setImagePreview]    = useState<string | null>(null);
  const [imageServerUrl,  setImageServerUrl]  = useState<string | null>(null);
  const [imageUploading,  setImageUploading]  = useState(false);
  const [dragOver,        setDragOver]        = useState(false);

  // ── Voice ──────────────────────────────────────────────────────────────────
  const [recordState,  setRecordState]  = useState<RecordState>('idle');
  const [recordSecs,   setRecordSecs]   = useState(0);
  const [voiceCloned,  setVoiceCloned]  = useState(false);

  // ── Persona ────────────────────────────────────────────────────────────────
  const [idleIntensity,     setIdleIntensity]     = useState<IdleIntensity>('normal');
  const [systemPrompt,      setSystemPrompt]      = useState('');
  const [vapiVoiceProvider, setVapiVoiceProvider] = useState('');
  const [vapiVoiceId,       setVapiVoiceId]       = useState('');

  // ── Preview ────────────────────────────────────────────────────────────────
  const [previewUrl,   setPreviewUrl]   = useState<string | null>(null);
  const [previewType,  setPreviewType]  = useState<'image' | 'video' | null>(null);
  const [generating,   setGenerating]   = useState(false);
  const [previewNote,  setPreviewNote]  = useState<string | null>(null);

  // ── Toast ──────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState<Toast | null>(null);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const fileInputRef       = useRef<HTMLInputElement>(null);
  const mediaRecorderRef   = useRef<MediaRecorder | null>(null);
  const audioChunksRef     = useRef<Blob[]>([]);
  const recordTimerRef     = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordAutoStopRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const micStreamRef       = useRef<MediaStream | null>(null);

  // ── Load profile on mount ──────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${API}/avatar/digital-twin`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((p: DigitalTwinProfile) => {
        setIdleIntensity(p.idle_intensity || 'normal');
        setSystemPrompt(p.system_prompt || '');
        setVapiVoiceProvider(p.vapi_voice_provider || '');
        setVapiVoiceId(p.vapi_voice_id || '');
        setVoiceCloned(p.has_voice_embedding);
        if (p.face_image_url) {
          // Add cache-busting so the latest uploaded image always loads
          setImageServerUrl(p.face_image_url);
          setImagePreview(`${API_ORIGIN}${p.face_image_url}?t=${Date.now()}`);
          setPreviewUrl(`${API_ORIGIN}${p.face_image_url}?t=${Date.now()}`);
          setPreviewType('image');
        }
      })
      .catch(() => {/* server may be unavailable — defaults are fine */})
      .finally(() => setLoading(false));
  }, []);

  // ── Image upload ───────────────────────────────────────────────────────────
  const uploadImage = useCallback(async (file: File) => {
    // Instant local preview
    const localUrl = URL.createObjectURL(file);
    setImagePreview(localUrl);

    setImageUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/avatar/upload-image`, { method: 'POST', body: fd });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const { url } = await r.json();
      setImageServerUrl(url);
      setPreviewUrl(`${API_ORIGIN}${url}?t=${Date.now()}`);
      setPreviewType('image');
      setToast({ msg: 'Reference image uploaded and processed.', sev: 'success' });
    } catch {
      setToast({ msg: 'Image upload failed — try again.', sev: 'error' });
    } finally {
      setImageUploading(false);
    }
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadImage(file);
    // Reset so re-selecting the same file still triggers onChange
    e.target.value = '';
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) uploadImage(file);
  }, [uploadImage]);

  // ── Voice recording ────────────────────────────────────────────────────────
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      audioChunksRef.current = [];

      const recorder = new MediaRecorder(stream);
      recorder.ondataavailable = e => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        micStreamRef.current = null;

        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setRecordState('processing');

        try {
          const fd = new FormData();
          fd.append('file', blob, 'voice_sample.webm');
          const r = await fetch(`${API}/avatar/voice-sample`, { method: 'POST', body: fd });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const result = await r.json();
          setVoiceCloned(result.has_embedding === true);
          setRecordState(result.has_embedding ? 'done' : 'error');
          setToast({
            msg: result.has_embedding
              ? 'Voice sample processed — embedding saved.'
              : `Voice processing failed: ${result.message ?? 'unknown error'}`,
            sev: result.has_embedding ? 'success' : 'error',
          });
        } catch {
          setRecordState('error');
          setToast({ msg: 'Voice upload failed — try again.', sev: 'error' });
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start(200); // collect in 200 ms chunks
      setRecordState('recording');
      setRecordSecs(0);

      // Tick timer
      recordTimerRef.current = setInterval(() => {
        setRecordSecs(s => s + 1);
      }, 1000);

      // Auto-stop at MAX_RECORD_S
      recordAutoStopRef.current = setTimeout(stopRecording, MAX_RECORD_S * 1000);

    } catch {
      setToast({ msg: 'Microphone access denied or unavailable.', sev: 'error' });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopRecording = useCallback(() => {
    if (recordTimerRef.current)    { clearInterval(recordTimerRef.current);   recordTimerRef.current = null; }
    if (recordAutoStopRef.current) { clearTimeout(recordAutoStopRef.current); recordAutoStopRef.current = null; }
    mediaRecorderRef.current?.stop();
    mediaRecorderRef.current = null;
  }, []);

  // Cleanup on unmount
  useEffect(() => () => {
    stopRecording();
    micStreamRef.current?.getTracks().forEach(t => t.stop());
  }, [stopRecording]);

  // ── Preview generation ─────────────────────────────────────────────────────
  const handleGeneratePreview = useCallback(async () => {
    if (!imageServerUrl) {
      setToast({ msg: 'Upload a reference image first.', sev: 'info' });
      return;
    }
    setGenerating(true);
    setPreviewNote(null);
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 20 * 60 * 1000); // 20-min ceiling
      let r: Response;
      try {
        r = await fetch(`${API}/avatar/generate-preview`, { method: 'POST', signal: ctrl.signal });
      } finally {
        clearTimeout(timer);
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const result = await r.json();
      if (result.status === 'error') throw new Error(result.message);
      setPreviewUrl(
        result.type === 'video'
          ? `${API_ORIGIN}/api/v1/avatar/preview-video?t=${Date.now()}`
          : `${API_ORIGIN}${result.url}?t=${Date.now()}`,
      );
      setPreviewType(result.type);
      setPreviewNote(result.note ?? null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Preview failed.';
      setToast({ msg, sev: 'error' });
    } finally {
      setGenerating(false);
    }
  }, [imageServerUrl]);

  // ── Save persona settings ──────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/avatar/digital-twin`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          idle_intensity:      idleIntensity,
          system_prompt:       systemPrompt,
          vapi_voice_provider: vapiVoiceProvider.trim(),
          vapi_voice_id:       vapiVoiceId.trim(),
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setToast({ msg: 'Digital twin profile saved!', sev: 'success' });
    } catch {
      setToast({ msg: 'Save failed — try again.', sev: 'error' });
    } finally {
      setSaving(false);
    }
  }, [idleIntensity, systemPrompt, vapiVoiceProvider, vapiVoiceId]);

  // ─────────────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────────────

  const fmtSecs = (s: number) =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const recordLabel = () => {
    if (recordState === 'recording')  return `Recording… ${fmtSecs(recordSecs)} / ${fmtSecs(MAX_RECORD_S)}`;
    if (recordState === 'processing') return 'Processing…';
    if (recordState === 'done')       return 'Voice cloned ✓';
    if (recordState === 'error')      return 'Recording failed';
    return 'Start Recording';
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', py: 3 }}>

      {/* ── Page header ─────────────────────────────────────────────────── */}
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Neural Avatar Builder
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Create your AI Digital Twin — upload a headshot, clone your voice, and define your
        assistant's personality. The avatar in every AI agent call will mirror this profile.
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>

        {/* ════════════════════════════════════════════════════════════════
            LEFT COLUMN — Identity + Voice
        ════════════════════════════════════════════════════════════════ */}
        <Box sx={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* ── Step 1: Identity ─────────────────────────────────────────── */}
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <SectionHeader
              step="1"
              title="Identity"
              subtitle="Upload a high-quality headshot. The image will be cropped to 512×512 for the MuseTalk engine."
            />

            {/* Drop zone */}
            <Box sx={{ p: 2 }}>
              <Box
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                sx={{
                  border: '2px dashed',
                  borderColor: dragOver ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  minHeight: 200,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  bgcolor: dragOver ? 'action.hover' : 'background.default',
                  transition: 'all 0.18s ease',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {imagePreview ? (
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Reference headshot"
                    sx={{
                      width:     '100%',
                      height:    '100%',
                      objectFit: 'cover',
                      display:   'block',
                      minHeight: 200,
                    }}
                  />
                ) : (
                  <>
                    <Person sx={{ fontSize: 56, color: 'grey.400', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Drop a professional headshot here
                    </Typography>
                    <Typography variant="caption" color="text.disabled" mt={0.5}>
                      or click to browse — JPG, PNG, WEBP
                    </Typography>
                  </>
                )}
                {imageUploading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0,0,0,0.45)',
                    }}
                  >
                    <CircularProgress color="inherit" sx={{ color: 'white' }} />
                  </Box>
                )}
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileInput}
              />
              <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CloudUpload />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={imageUploading}
                  sx={{ flex: 1 }}
                >
                  {imagePreview ? 'Change Photo' : 'Upload Photo'}
                </Button>
                {imageServerUrl && (
                  <Tooltip title="Image uploaded and pre-processed">
                    <CheckCircle color="success" sx={{ alignSelf: 'center' }} />
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Paper>

          {/* ── Step 2: Voice DNA ─────────────────────────────────────────── */}
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <SectionHeader
              step="2"
              title="Voice DNA"
              subtitle={`Record 10–${MAX_RECORD_S} seconds of your voice. The backend extracts an XTTS v2 speaker embedding so the AI sounds exactly like you.`}
            />
            <Box sx={{ p: 2 }}>
              {/* Recording progress bar */}
              {recordState === 'recording' && (
                <LinearProgress
                  variant="determinate"
                  value={(recordSecs / MAX_RECORD_S) * 100}
                  sx={{ mb: 1.5, borderRadius: 1 }}
                />
              )}

              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Main record / stop button */}
                {recordState !== 'recording' ? (
                  <Button
                    variant="contained"
                    color={recordState === 'done' ? 'success' : 'primary'}
                    startIcon={
                      recordState === 'processing' ? <CircularProgress size={16} color="inherit" /> :
                      recordState === 'done'        ? <CheckCircle /> :
                      <Mic />
                    }
                    onClick={recordState === 'done' || recordState === 'error' ? startRecording : startRecording}
                    disabled={recordState === 'processing'}
                    sx={{ flex: 1 }}
                  >
                    {recordLabel()}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<Stop />}
                    onClick={stopRecording}
                    sx={{ flex: 1 }}
                  >
                    Stop &amp; Process
                  </Button>
                )}

                {/* Re-record */}
                {(recordState === 'done' || recordState === 'error') && (
                  <Tooltip title="Record again">
                    <Button
                      variant="outlined"
                      color="inherit"
                      size="small"
                      onClick={() => setRecordState('idle')}
                    >
                      <Refresh fontSize="small" />
                    </Button>
                  </Tooltip>
                )}
              </Box>

              {/* Status hint */}
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                {recordState === 'idle'       && 'Say: "Hello, I\'m your digital assistant. How can I help you today?"'}
                {recordState === 'recording'  && `${MAX_RECORD_S - recordSecs}s remaining — speak naturally.`}
                {recordState === 'processing' && 'Extracting speaker embedding…'}
                {recordState === 'done'       && (voiceCloned ? '✓ Embedding saved. The AI will use your voice tonality.' : 'Sample received.')}
                {recordState === 'error'      && 'Recording failed — click to try again.'}
              </Typography>
            </Box>
          </Paper>
        </Box>

        {/* ════════════════════════════════════════════════════════════════
            RIGHT COLUMN — Persona + Preview
        ════════════════════════════════════════════════════════════════ */}
        <Box sx={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* ── Step 3: Persona ───────────────────────────────────────────── */}
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <SectionHeader
              step="3"
              title="Persona"
              subtitle="Define how the avatar behaves during AI agent calls."
            />
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

              {/* Idle intensity */}
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.75}>
                  Idle Loop Intensity
                </Typography>
                <ButtonGroup size="small" fullWidth>
                  {(['calm', 'normal', 'energetic'] as IdleIntensity[]).map(v => (
                    <Button
                      key={v}
                      variant={idleIntensity === v ? 'contained' : 'outlined'}
                      onClick={() => setIdleIntensity(v)}
                      sx={{ textTransform: 'capitalize' }}
                    >
                      {v}
                    </Button>
                  ))}
                </ButtonGroup>
                <Typography variant="caption" color="text.disabled" mt={0.5} display="block">
                  {idleIntensity === 'calm'      && 'Slow blinking, minimal head movement — professional and serene.'}
                  {idleIntensity === 'normal'    && 'Natural pacing — suitable for most use cases.'}
                  {idleIntensity === 'energetic' && 'Livelier micro-expressions — great for sales/support roles.'}
                </Typography>
              </Box>

              <Divider />

              {/* System prompt */}
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.75}>
                  AI System Prompt (Personality)
                </Typography>
                <TextField
                  multiline
                  rows={6}
                  fullWidth
                  size="small"
                  placeholder={
                    'Example:\n' +
                    'You are Alex, a friendly and professional support specialist at Acme Corp. ' +
                    'You always greet customers warmly, listen carefully to their issues, and ' +
                    'provide clear, concise solutions. You never rush the customer and always ' +
                    'confirm they are satisfied before ending the call.'
                  }
                  value={systemPrompt}
                  onChange={e => setSystemPrompt(e.target.value)}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
                />
                <Typography variant="caption" color="text.disabled" mt={0.5} display="block">
                  This prompt is synced to the Vapi AI assistant configuration when the call starts.
                </Typography>
              </Box>

              <Divider />

              {/* Vapi voice override */}
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.75}>
                  Vapi Voice Override
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Provider</InputLabel>
                    <Select
                      label="Provider"
                      value={vapiVoiceProvider}
                      onChange={e => setVapiVoiceProvider(e.target.value)}
                    >
                      <MenuItem value="">Default (Riley)</MenuItem>
                      <MenuItem value="elevenlabs">ElevenLabs</MenuItem>
                      <MenuItem value="playht">PlayHT</MenuItem>
                      <MenuItem value="deepgram">Deepgram</MenuItem>
                      <MenuItem value="azure">Azure</MenuItem>
                      <MenuItem value="vapi">Vapi built-in</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    fullWidth
                    label="Voice ID"
                    placeholder="e.g. EXAVITQu4vr4xnSDxMaL"
                    value={vapiVoiceId}
                    onChange={e => setVapiVoiceId(e.target.value)}
                    disabled={!vapiVoiceProvider}
                  />
                </Box>
                <Typography variant="caption" color="text.disabled" mt={0.5} display="block">
                  Paste your cloned voice ID from ElevenLabs (or another provider) to replace Riley during AI agent calls.
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* ── Preview panel ─────────────────────────────────────────────── */}
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: '#0a0a18' }}
          >
            <Box sx={{
              px: 2, py: 1.5,
              bgcolor: 'rgba(255,255,255,0.04)',
              borderBottom: '1px solid rgba(255,255,255,0.07)',
            }}>
              <Typography variant="subtitle2" fontWeight={700} color="grey.100">
                Live Preview
              </Typography>
              <Typography variant="caption" color="grey.500">
                {generating ? 'Running MuseTalk inference (5–15 min on CPU)…'
                  : previewType === 'video' ? 'MuseTalk idle animation'
                  : 'Static reference — click Generate for idle animation'}
              </Typography>
            </Box>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 220,
              p: 2,
              position: 'relative',
            }}>
              {previewUrl && previewType === 'video' ? (
                <Box
                  component="video"
                  src={previewUrl}
                  autoPlay
                  loop
                  muted
                  playsInline
                  sx={{ maxWidth: '100%', maxHeight: 220, borderRadius: 1 }}
                />
              ) : previewUrl && previewType === 'image' ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Avatar preview"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 220,
                    borderRadius: 1,
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <Box sx={{ textAlign: 'center' }}>
                  <Person sx={{ fontSize: 64, color: 'grey.700' }} />
                  <Typography variant="caption" color="grey.600" display="block">
                    Upload a headshot to see preview
                  </Typography>
                </Box>
              )}
              {generating && (
                <Box sx={{
                  position: 'absolute', inset: 0,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5,
                  bgcolor: 'rgba(0,0,0,0.72)',
                }}>
                  <CircularProgress sx={{ color: 'white' }} />
                  <Typography variant="caption" sx={{ color: 'grey.300', textAlign: 'center', px: 2 }}>
                    Running MuseTalk inference…<br />This takes 5–15 min on CPU. Please wait.
                  </Typography>
                </Box>
              )}
            </Box>
            {previewNote && (
              <Box sx={{ px: 2, pb: 1.5 }}>
                <Typography variant="caption" color="grey.500">{previewNote}</Typography>
              </Box>
            )}
            <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={generating ? <CircularProgress size={14} /> : <PlayArrow />}
                onClick={handleGeneratePreview}
                disabled={generating || !imageServerUrl}
                sx={{ color: 'grey.300', borderColor: 'rgba(255,255,255,0.15)', flex: 1 }}
              >
                {generating ? 'Generating…' : 'Generate Preview'}
              </Button>
            </Box>
          </Paper>

          {/* ── Save ─────────────────────────────────────────────────────── */}
          <Button
            variant="contained"
            size="large"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
            onClick={handleSave}
            disabled={saving}
            fullWidth
          >
            {saving ? 'Saving…' : 'Save Digital Twin'}
          </Button>

          {/* ── Workflow summary ──────────────────────────────────────────── */}
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>
              Digital Twin Status
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <StatusRow
                label="Reference Image"
                done={!!imageServerUrl}
                pending="Upload a headshot in Step 1"
              />
              <StatusRow
                label="Voice Embedding"
                done={voiceCloned}
                pending="Record a voice sample in Step 2"
              />
              <StatusRow
                label="Persona Configured"
                done={systemPrompt.trim().length > 20}
                pending="Add a system prompt in Step 3"
              />
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* ── Toast ────────────────────────────────────────────────────────── */}
      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.sev} onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ─── Small sub-components ─────────────────────────────────────────────────────

const SectionHeader: React.FC<{ step: string; title: string; subtitle: string }> = ({ step, title, subtitle }) => (
  <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
      <Box
        sx={{
          width: 22, height: 22, borderRadius: '50%',
          bgcolor: 'primary.main', color: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 700, flexShrink: 0,
        }}
      >
        {step}
      </Box>
      <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
    </Box>
    <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
  </Box>
);

const StatusRow: React.FC<{ label: string; done: boolean; pending: string }> = ({ label, done, pending }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
    <CheckCircle
      fontSize="small"
      sx={{ color: done ? 'success.main' : 'action.disabled', flexShrink: 0, mt: 0.1 }}
    />
    <Box>
      <Typography variant="caption" fontWeight={600} display="block">{label}</Typography>
      {!done && (
        <Typography variant="caption" color="text.disabled">{pending}</Typography>
      )}
    </Box>
  </Box>
);

export default AvatarCustomizer;
